import React, { useRef, useCallback, useEffect } from 'react';
import { arrayOf, func, number, shape, string } from 'prop-types';
import classNames from 'classnames';
import debounce from 'lodash/debounce';

import { FieldTextInput } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './KeywordFilter.module.css';

// When user types, we wait for new keystrokes a while before searching new content
const DEBOUNCE_WAIT_TIME = 600;
// Short search queries (e.g. 2 letters) have a longer timeout before search is made
const TIMEOUT_FOR_SHORT_QUERIES = 2000;

const getKeywordQueryParam = queryParamNames => {
  return Array.isArray(queryParamNames)
    ? queryParamNames[0]
    : typeof queryParamNames === 'string'
    ? queryParamNames
    : 'keywords';
};

function KeywordFilter({
  rootClassName = null,
  className = null,
  id,
  name,
  label,
  initialValues = null,
  contentPlacementOffset = 0,
  onSubmit,
  queryParamNames,
  showAsPopup,
  ...rest
}) {
  const filterRef = useRef(null);
  const filterContentRef = useRef(null);
  const mobileInputRef = useRef(null);
  const shortKeywordTimeout = useRef(null);

  useEffect(() => {
    return () => {
      window.clearTimeout(shortKeywordTimeout.current);
    };
  }, []);

  const positionStyleForContent = useCallback(() => {
    const filter = filterRef.current;
    const filterContent = filterContentRef.current;
    if (filter && filterContent) {
      // Render the filter content to the right from the menu
      // unless there's no space in which case it is rendered
      // to the left
      const distanceToRight = window.innerWidth - filter.getBoundingClientRect().right;
      const labelWidth = filter.offsetWidth;
      const contentWidth = filterContent.offsetWidth;
      const contentWidthBiggerThanLabel = contentWidth - labelWidth;
      const renderToRight = distanceToRight > contentWidthBiggerThanLabel;
      const offset = renderToRight
        ? { left: contentPlacementOffset }
        : { right: contentPlacementOffset };
      // set a min-width if the content is narrower than the label
      const minWidth = contentWidth < labelWidth ? { minWidth: labelWidth } : null;

      return { ...offset, ...minWidth };
    }
    return {};
  }, [contentPlacementOffset]);

  const classes = classNames(rootClassName || css.root, className);

  const urlParam = getKeywordQueryParam(queryParamNames);
  const hasInitialValues =
    !!initialValues && !!initialValues[urlParam] && initialValues[urlParam].length > 0;
  const labelForPopup = hasInitialValues ? `Keyword: ${initialValues[urlParam]}` : label;

  const labelClass = hasInitialValues ? css.labelPlainSelected : css.labelPlain;
  const labelForPlain = <span className={labelClass}>{label}</span>;

  const filterText = 'Keyword';

  const placeholder = 'Enter keyword...';

  const contentStyle = positionStyleForContent();

  // pass the initial values with the name key so that
  // they can be passed to the correct field
  const namedInitialValues = { [name]: initialValues ? initialValues[urlParam] : '' };

  const handleSubmit = useCallback(
    values => {
      const usedValue = values ? values[name] : values;
      onSubmit({ [urlParam]: usedValue });
    },
    [onSubmit, name, urlParam]
  );

  const debouncedSubmit = useCallback(
    debounce(handleSubmit, DEBOUNCE_WAIT_TIME, {
      leading: false,
      trailing: true,
    }),
    [handleSubmit]
  );

  // Use timeout for shart queries and debounce for queries with any length
  const handleChangeWithDebounce = useCallback(
    values => {
      // handleSubmit gets values as params.
      // If this field ("keyword") is short, create timeout
      const hasKeywordValue = values && values[name];
      const keywordValue = hasKeywordValue ? values[name] : '';
      if (!hasKeywordValue || (hasKeywordValue && keywordValue.length >= 3)) {
        if (shortKeywordTimeout.current) {
          window.clearTimeout(shortKeywordTimeout.current);
        }
        return debouncedSubmit(values);
      } else {
        shortKeywordTimeout.current = window.setTimeout(() => {
          // if mobileInputRef exists, use the most up-to-date value from there
          return mobileInputRef && mobileInputRef.current
            ? handleSubmit({ ...values, [name]: mobileInputRef.current.value })
            : handleSubmit(values);
        }, TIMEOUT_FOR_SHORT_QUERIES);
      }
    },
    [debouncedSubmit, handleSubmit, name]
  );

  // Uncontrolled input needs to be cleared through the reference to DOM element.
  const handleClear = useCallback(() => {
    if (mobileInputRef && mobileInputRef.current) {
      mobileInputRef.current.value = '';
    }
  }, []);

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      popupClassName={css.popupSize}
      name={name}
      label={labelForPopup}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      showAsPopup
      labelMaxWidth={250}
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      <FieldTextInput
        className={css.field}
        name={name}
        id={`${id}-input`}
        type="text"
        label={filterText}
        placeholder={placeholder}
        autoComplete="off"
      />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={labelForPlain}
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      contentPlacementOffset={contentStyle}
      onSubmit={handleChangeWithDebounce}
      onClear={handleClear}
      initialValues={namedInitialValues}
      {...rest}
    >
      <fieldset className={css.fieldPlain}>
        <label className={css.fieldPlainLabel}>{filterText}</label>
        <FieldTextInput
          name={name}
          id={`${id}-input`}
          className={css.fieldPlainInput}
          inputRef={mobileInputRef}
          type="text"
          placeholder={placeholder}
          autoComplete="off"
        />
      </fieldset>
    </FilterPlain>
  );
}



KeywordFilter.propTypes = {
  rootClassName: string,
  className: string,
  id: string.isRequired,
  name: string.isRequired,
  queryParamNames: arrayOf(string).isRequired,
  label: string.isRequired,
  onSubmit: func.isRequired,
  initialValues: shape({
    keyword: string,
  }),
  contentPlacementOffset: number,
};

export default KeywordFilter;
